import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Types for common API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  success: false;
  statusCode: number;
  message: string;
  errors?: string[];
  timestamp: string;
}

// Base API URL. REACT_APP_API_URL is a CRA build-time env var — when it
// isn't set (as in the AWS build), this falls back to '/api', which must
// stay root-relative. '../api' is directory-relative: the browser resolves
// it against the CURRENT route, not the site root, so on a nested route like
// /billing/invoices/39 it silently resolves to /billing/api/billing/invoices/39
// instead of /api/billing/invoices/39, and the backend's SPA static-file
// fallback serves index.html instead of hitting the real controller.
const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Request interceptor - add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const activeWarehouseId = localStorage.getItem('activeWarehouseId');
    if (activeWarehouseId && config.headers) {
      config.headers['x-warehouse-id'] = activeWarehouseId;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Helper function to extract resource name from URL and method
const getPermissionMessage = (url?: string, method?: string): string => {
  if (!url) return 'Access Denied: You don\'t have permission to perform this action. Please contact your administrator.';
  
  // Extract the main resource from the URL path
  const urlPath = url.replace(/^\//, '').split('?')[0]; // Remove leading slash and query params
  const segments = urlPath.split('/');
  
  // Map of URL patterns to resource names
  const resourceMap: Record<string, string> = {
    'warehouses': 'warehouse management',
    'zones': 'zone management',
    'racks': 'rack management',
    'locations': 'location management',
    'clients': 'client management',
    'grn': 'GRN (Goods Receipt Note)',
    'dispatch': 'dispatch management',
    'inventory': 'inventory records',
    'gate-entries': 'gate entry records',
    'users': 'user management',
    'roles': 'role management',
    'permissions': 'permission settings',
    'products': 'product management',
    'reports': 'reports',
    'dashboard': 'dashboard',
    'audit-report': 'audit reports',
    'inventory-summary': 'inventory summary',
    'product-categories': 'product categories',
    'product-types': 'product types',
  };
  
  // Determine action based on HTTP method
  const actionMap: Record<string, string> = {
    'GET': 'view',
    'POST': 'create',
    'PUT': 'update',
    'PATCH': 'update',
    'DELETE': 'delete',
  };
  
  // Find the resource name
  let resourceName = 'this resource';
  for (const segment of segments) {
    if (resourceMap[segment]) {
      resourceName = resourceMap[segment];
      break;
    }
  }
  
  const action = actionMap[method?.toUpperCase() || 'GET'] || 'access';
  
  return `Access Denied: You don't have permission to ${action} ${resourceName}. Please contact your administrator if you need access.`;
};

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Return the data directly from the response wrapper
    return response.data;
  },
  (error: AxiosError<ApiError>) => {
    // Handle different error scenarios
    if (error.response) {
      // Server responded with error
      const apiError: ApiError = error.response.data;
      
      // Handle 403 - Forbidden
      if (error.response.status === 403) {
        // Only show the generic "you don't have permission" message when the 403
        // came from the permissions guard (generic message) or has no message.
        // Domain 403s (e.g. "Warehouse does not belong to this tenant") carry their
        // own specific message and must NOT be masked as a permission error.
        const backendMessage = typeof apiError?.message === 'string' ? apiError.message : '';
        const genericGuardMessages = [
          'You do not have permission to access this resource',
          'Forbidden',
          'Forbidden resource',
        ];
        const message = !backendMessage || genericGuardMessages.includes(backendMessage)
          ? getPermissionMessage(error.config?.url, error.config?.method)
          : backendMessage;
        return Promise.reject({
          ...apiError,
          message,
        } as ApiError);
      }
      
      // Handle 401 - unauthorized (token expired or invalid)
      // Only redirect if user is logged in and token is invalid
      // Don't redirect for login/register failures
      if (error.response.status === 401) {
        const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                               error.config?.url?.includes('/auth/register') ||
                               error.config?.url?.includes('/auth/verify-otp') ||
                               error.config?.url?.includes('/auth/resend-otp');
        
        // Only clear session and redirect if it's NOT a login/register attempt
        if (!isAuthEndpoint && localStorage.getItem('authToken')) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('userData');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('isLoggedIn');
          window.location.href = '/login';
        }
      }
      
      return Promise.reject(apiError);
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        success: false,
        statusCode: 0,
        message: 'Network error. Please check your connection.',
        timestamp: new Date().toISOString(),
      } as ApiError);
    } else {
      // Something else happened
      return Promise.reject({
        success: false,
        statusCode: 0,
        message: error.message || 'An unexpected error occurred',
        timestamp: new Date().toISOString(),
      } as ApiError);
    }
  }
);

// API service methods
export const api = {
  // Generic methods
  get: <T = any>(url: string, config?: any): Promise<ApiResponse<T>> => 
    apiClient.get(url, config),
  
  post: <T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> => 
    apiClient.post(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> => 
    apiClient.put(url, data, config),
  
  patch: <T = any>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> => 
    apiClient.patch(url, data, config),
  
  delete: <T = any>(url: string, config?: any): Promise<ApiResponse<T>> => 
    apiClient.delete(url, config),
};

export default apiClient;
